// A "Ducks"-style collection
// https://github.com/erikras/ducks-modular-redux

import { getMessage } from 'util/locale';

import { treatErrorAs } from 'api/errors';
import { trackError } from 'util/error';

// Constants
import { errorLevel } from 'constants/errors';
import { exportScopeTypes } from 'constants/exportSelector';
import { mycroftAffiliateIds } from 'constants/affiliates';
import { exportTypes, messageIds } from 'constants/values';

// selectors
import {
  getJohnDeereFields,
  getScriptsForWorkPlansCreation,
  hasAffiliateById,
} from 'affiliates/selectors';
import {
  getActiveFieldId,
  getActiveOrganizationIdForAffiliateId,
  getActiveUserId,
  getActiveProductYear,
  getActiveScriptId,
  getActiveFieldUuid,
} from 'current/selectors';
import { getExportParams } from 'export-selector/selectors';
import { getFieldIdsForOperation } from 'fields/selectors';
import { getScriptById, isValidScript } from 'scripts/selectors';
import { getUnitsOfMeasureTemplate } from 'user/selectors';

// actions
import {
  closeConnectToAffiliateModal,
  closeSelectAffiliateOrganizationModal,
  openConnectToAffiliateModal,
  openSelectAffiliateOrganizationModal,
  setExportOptionsIsOpen,
  setScriptOptionsIsOpen,
} from 'current/uiwidgets/';
import { getPreferredDeviceIdForBulkSendToAffiliate } from 'devices/selectors';
import {
  addErrorToast,
  addSuccessToast,
  addTransitionModal,
  removeTransitionModal,
} from 'messages';
import { requestJohnDeereWorkPlans } from './requesters';

// loaders/senders
import { fetchStatusSendScript } from 'affiliates/loaders';
import {
  sendBeginBulkSendScriptsToAffiliate,
  sendBeginSingleSendScriptToAffiliate,
  sendBeginWorkPlanToJohnDeere,
  sendConnectToAffiliate,
} from 'affiliates/senders';
import { Affiliates, JohnDeereField, Store, WorkPlans } from 'store/types/types';

// Action Types
export const typePrefix = 'vra/affiliates/';
export const actions = {
  'SET_AFFILIATE_DATA': `${typePrefix}SET_AFFILIATE_DATA`,
  'SET_JOHN_DEERE_FIELDS': `${typePrefix}SET_JOHN_DEERE_FIELDS`,
  'SET_SELECTED_JOHN_DEERE_FIELD': `${typePrefix}SET_SELECTED_JOHN_DEERE_FIELD`,
  'SET_JOHN_DEERE_WORK_PLANS': `${typePrefix}SET_JOHN_DEERE_WORK_PLANS`,
  'SET_FV_FIELD_FOR_JOHN_DEERE_FIELD': `${typePrefix}SET_FV_FIELD_FOR_JOHN_DEERE_FIELD`
};

type Action = {
  type?: keyof typeof actions;
  payload?: {
    fvFieldUuid: string;
    johnDeereFieldId: string;
  };
}

// Reducer
export default function reducer(state: Affiliates = { connections: [] }, action: Action = {}) {
  switch (action.type) {
    case actions.SET_AFFILIATE_DATA:
      return { ...state, ...action.payload };
    case actions.SET_JOHN_DEERE_FIELDS:
      return {
        ...state,
        deere: {
          ...state.deere,
          ...action.payload,
        },
      };
    case actions.SET_SELECTED_JOHN_DEERE_FIELD:
      return {
        ...state,
        deere: {
          ...state.deere,
          selectedField: action.payload || '',
        },
      };
    case actions.SET_JOHN_DEERE_WORK_PLANS:
      return {
        ...state,
        deere: {
          ...state.deere,
          workPlans: {
            byScriptId: {
              ...state.deere.workPlans.byScriptId,
              ...action.payload,
            },
          },
        },
      };
    case actions.SET_FV_FIELD_FOR_JOHN_DEERE_FIELD:
      const { fields } = structuredClone(state.deere);
      const previousMatch = Object.values(fields.byId).find(
        ({ fvFieldUuid }) => fvFieldUuid === action.payload.fvFieldUuid,
      );

      fields.byId[action.payload.johnDeereFieldId].fvFieldUuid
        = action.payload.fvFieldUuid;

      if (previousMatch) {
        fields.byId[previousMatch.id].fvFieldUuid = null;
      }

      return {
        ...state,
        deere: {
          ...state.deere,
          fields,
        },
      };

    default:
      return state;
  }
}

// Action Creators

export const setAffiliateList = (affiliates: Affiliates) => {
  return {
    type: actions.SET_AFFILIATE_DATA,
    payload: affiliates,
  };
};

export const setJohnDeereFields = (fields: JohnDeereField[]) => {
  return {
    type: actions.SET_JOHN_DEERE_FIELDS,
    payload: fields,
  };
};

export const setSelectedJohnDeereField = (fieldId?: string) => {
  return {
    type: actions.SET_SELECTED_JOHN_DEERE_FIELD,
    payload: fieldId,
  };
};

export const setFVFieldForJohnDeereField = ({
  fvFieldUuid,
  johnDeereFieldId,
}) => {
  return {
    type: actions.SET_FV_FIELD_FOR_JOHN_DEERE_FIELD,
    payload: { fvFieldUuid, johnDeereFieldId },
  };
};

export const setJohnDeereWorkPlans = (workPlans: WorkPlans) => {
  return {
    type: actions.SET_JOHN_DEERE_WORK_PLANS,
    payload: workPlans,
  };
};

export const sendToAffiliate = ({ affiliateId, scriptId }) => {
  return (dispatch: (callback: unknown) => void, getState: () => Store) => {
    const state = getState();
    const script = getScriptById(state, scriptId);
    const fieldId = getActiveFieldId(state);
    const organizationId = getActiveOrganizationIdForAffiliateId(
      state,
      affiliateId,
    );
    const userId = getActiveUserId(state);
    const unitTemplate = getUnitsOfMeasureTemplate(state);

    const sendIsValid = isValidScript(state, script);

    if (!sendIsValid) {
      // TODO: highlight zones validation
      dispatch(
        addErrorToast(
          getMessage('help-text__rejection--send', { target: affiliateId }),
        ),
      );
      return null;
    }

    const modalId = messageIds.SEND_TO_AFFILIATE;

    // close options and modals that might be open
    dispatch(setScriptOptionsIsOpen({ scriptId, status: false }));
    dispatch(closeSelectAffiliateOrganizationModal());

    if (organizationId) {
      return Promise.resolve()
      .then(() => {
        // send single script
        dispatch(
          addTransitionModal({
            id: modalId,
            bodyHtmlMessageKey: 'text__in-progress--send',
            bodyHtmlMessageParams: { scriptCount: 1, target: affiliateId },
          // eslint-disable-next-line
          } as any),
        );
        return sendBeginSingleSendScriptToAffiliate({
          affiliateId: mycroftAffiliateIds[affiliateId],
          fieldId,
          organizationId,
          scriptId,
          userId,
          unitTemplate,
        });
      })
      .then((jobUuid) => fetchStatusSendScript({ jobUuid, userId })) // poll status
      .then(() => {
        dispatch(removeTransitionModal({ id: modalId }));
        dispatch(
          addSuccessToast(
            getMessage('text__success--send', { target: affiliateId }),
          ),
        );
      })
      .catch(
        treatErrorAs({
          severity: errorLevel.WARN,
          messageKey: 'help-text__failure--send',
          andHandleNow: true,
          andStopHere: false,
        // eslint-disable-next-line
        } as any),
      )
      .catch(() => {
        dispatch(removeTransitionModal({ id: modalId }));
      });
    }

    dispatch(openSelectAffiliateOrganizationModal(affiliateId));
    return null;
  };
};

export const bulkSendToAffiliate = ({ affiliateId }) => {
  return (dispatch: (callback: unknown) => void, getState: () => Store) => {
    const state = getState();
    const organizationId = getActiveOrganizationIdForAffiliateId(
      state,
      affiliateId,
    );
    const preferredDeviceId = getPreferredDeviceIdForBulkSendToAffiliate(
      state,
      affiliateId,
    );
    const userId = getActiveUserId(state);
    const activeFieldIds = [parseInt(getActiveFieldId(state), 10)];
    const season = getActiveProductYear(state);
    const modalId = messageIds.SEND_TO_AFFILIATE;
    const { scopeType, scriptType, primaryOnly, operationUuid }
      = getExportParams(state);
    const operationFieldIds = getFieldIdsForOperation(state, operationUuid);
    const fieldIds
      = scopeType === exportScopeTypes.OPERATION
        ? operationFieldIds
        : activeFieldIds;
    const unitTemplate = getUnitsOfMeasureTemplate(state);

    // close options and modals that might be open
    dispatch(
      setExportOptionsIsOpen({ exportType: exportTypes.BULK, status: false }),
    );
    dispatch(closeSelectAffiliateOrganizationModal());

    if (!organizationId) {
      dispatch(openSelectAffiliateOrganizationModal(affiliateId));
      return null;
    }

    return (
      Promise.resolve()
      .then(() => {
        dispatch(
          addTransitionModal({
            id: modalId,
            bodyHtmlMessageKey: 'text__in-progress--send--bulk',
            bodyHtmlMessageParams: {
              scriptCount: 2 /* plural */,
              target: affiliateId,
            },
          // eslint-disable-next-line
          } as any),
        );

        // request download jobuuid
        return sendBeginBulkSendScriptsToAffiliate({
          affiliateId: mycroftAffiliateIds[affiliateId],
          fieldIds,
          organizationId,
          preferredDeviceId,
          primaryOnly,
          scriptType,
          season,
          userId,
          unitTemplate,
        });
      })
      .then((jobUuid) => fetchStatusSendScript({ jobUuid, userId })) // poll status
      .then(() => {
        // append iframe for testing if successful
        dispatch(removeTransitionModal({ id: modalId }));
        dispatch(
          addSuccessToast(
            getMessage('text__success--send', { target: affiliateId }),
          ),
        );
      })
      .catch(
        treatErrorAs({
          severity: errorLevel.WARN,
          messageKey: 'help-text__failure--send',
          andHandleNow: true,
          andStopHere: false,
        // eslint-disable-next-line
        } as any),
      )
      // dispatch an error if jobuuid request failed or if job fails or times out
      .catch(() => {
        dispatch(removeTransitionModal({ id: modalId }));
      })
    );
  };
};

export const sendToAffiliateForExportType = ({
  exportType,
  affiliateId,
  scriptId,
}) => {
  return (dispatch: (callback: unknown) => void, getState: () => Store) => {
    const state = getState();
    if (hasAffiliateById(state, affiliateId)) {
      if (exportType === exportTypes.SINGLE) {
        dispatch(sendToAffiliate({ affiliateId, scriptId }));
      } else {
        dispatch(bulkSendToAffiliate({ affiliateId }));
      }
    } else {
      dispatch(openConnectToAffiliateModal(affiliateId));
    }
  };
};

export const connectToAffiliate = ({ affiliateId }) => {
  return (dispatch: (callback: unknown) => void, getState: () => Store) => {
    const state = getState();
    const userId = getActiveUserId(state);

    dispatch(closeConnectToAffiliateModal());
    dispatch(
      addTransitionModal({
        id: messageIds.CONNECT_TO_AFFILIATE,
        bodyHtmlMessageKey: 'text__in-progress--redirect',
        bodyHtmlMessageParams: { target: affiliateId },
      // eslint-disable-next-line
      } as any),
    );
    return sendConnectToAffiliate({ affiliateId, userId })
    .then((redirectUrl: Location) => {
      dispatch(
        removeTransitionModal({ id: messageIds.CONNECT_TO_AFFILIATE }),
      );
      window.location = redirectUrl;
    })
    .catch(
      treatErrorAs({
        severity: errorLevel.WARN,
        messageKey: `help-text__failure--connection--${affiliateId}`,
        andHandleNow: true,
        andStopHere: false,
      // eslint-disable-next-line
      } as any),
    )
    .catch(() => {
      dispatch(
        removeTransitionModal({ id: messageIds.CONNECT_TO_AFFILIATE }),
      );
    });
  };
};

export const sendWorkPlanToJohnDeere = (johnDeereFieldId: string) => {
  return async (dispatch: (callback: unknown) => void, getState: () => Store) => {
    try {
      const state = getState();
      const fieldId = getActiveFieldId(state);
      const scriptId = getActiveScriptId(state);
      const johnDeereFields = getJohnDeereFields(state);
      const userId = getActiveUserId(state);
      const unitTemplate = getUnitsOfMeasureTemplate(state);
      const fieldUuid = getActiveFieldUuid(state);
      const { organizationId, id: jdFieldId } = johnDeereFields.find(
        ({ id, fvField }) =>
          johnDeereFieldId ? id === johnDeereFieldId : fvField === fieldUuid,
      );

      dispatch(
        addTransitionModal({
          id: messageIds.SEND_TO_AFFILIATE,
          bodyHtmlMessageKey: 'text__in-progress--create-jd-work-plan',
        // eslint-disable-next-line
        } as any),
      );

      const jobUuid = await sendBeginWorkPlanToJohnDeere({
        fieldId,
        jdFieldId,
        organizationId,
        scriptId,
        userId,
        unitTemplate,
      });

      await fetchStatusSendScript({ jobUuid, userId });
      dispatch(removeTransitionModal({ id: messageIds.SEND_TO_AFFILIATE }));
      dispatch(addSuccessToast(getMessage('text__success--send-JD-work-plan')));
      dispatch(requestJohnDeereWorkPlans());
    } catch (error) {
      dispatch(removeTransitionModal({ id: messageIds.SEND_TO_AFFILIATE }));
      treatErrorAs({
        severity: errorLevel.WARN,
        messageKey: 'help-text__failure--create-JD-work-plan',
        andHandleNow: true,
        andStopHere: true,
      // eslint-disable-next-line
      } as any)(error);
    }
  };
};

export const sendBulkWorkPlansToJohnDeere = () => {
  return async (dispatch: (callback: unknown) => void, getState: () => Store) => {
    const state = getState();
    const userId = getActiveUserId(state);
    const unitTemplate = getUnitsOfMeasureTemplate(state);
    const scriptsForWorkPlans = getScriptsForWorkPlansCreation(state);

    dispatch(
      addTransitionModal({
        id: messageIds.SEND_TO_AFFILIATE,
        bodyHtmlMessageKey: 'text__in-progress--create-jd-work-plan',
      // eslint-disable-next-line
      } as any),
    );

    const results
      = await Promise.allSettled(
        scriptsForWorkPlans.map(
          async ({
            'field-id': fieldId,
            jdFieldId,
            organizationId,
            id: scriptId,
          }) => {
            const jobUuid = await sendBeginWorkPlanToJohnDeere({
              fieldId,
              jdFieldId,
              organizationId,
              scriptId,
              userId,
              unitTemplate,
            });

            return fetchStatusSendScript({ jobUuid, userId });
          }
        )
      );

    const failedRequests = results.filter(
      ({ status }) => status === 'rejected',
    );
    const succeededRequests = results.filter(
      ({ status }) => status === 'fulfilled',
    );

    if (succeededRequests.length) {
      dispatch(requestJohnDeereWorkPlans());
    }

    dispatch(removeTransitionModal({ id: messageIds.SEND_TO_AFFILIATE }));

    if (failedRequests.length) {
      const reasonsList = failedRequests.reduce(
        (reasons, failedRequest, index) => {
          return (
            reasons
            + `${failedRequest.status === 'rejected'
              ? failedRequest.reason : ''}${index !== failedRequests.length - 1 ? ', '
              : ''}`
          );
        },
        '',
      );
      const workPlanError = new Error(
        `Errors found on ${failedRequests.length} out of ${results.length} work plans, due: ${reasonsList}`,
      );

      trackError(workPlanError);
      treatErrorAs({
        severity: errorLevel.WARN,
        messageKey:
          results.length === 1
            ? 'help-text__failure--create-JD-work-plan'
            : 'help-text__failure--create-JD-work-plan-bulk',
        andHandleNow: true,
        andStopHere: true,
      // eslint-disable-next-line
      } as any)(workPlanError);
    } else {
      dispatch(addSuccessToast(getMessage('text__success--send-JD-work-plan')));
    }
  };
};
